import React from 'react';
import cls from 'classnames';

import FocusTrap from 'focus-trap-react';
import type { TPAComponentProps } from '@wix/wix-ui-tpa/dist/cssVars/types/types';

import { Portal } from '../Portal';
import { Backdrop } from '../Backdrop';

import { useScrollBlock, useViewportResizer } from './viewport';

import classes from './Modal.scss';

interface IModalProps extends TPAComponentProps {
  isOpen?: boolean;
  children: React.ReactElement;

  scalable?: boolean;
  resize?: 'none' | 'native';
  backdrop?: boolean;
  hideBackdrop?: boolean;
  keepMounted?: boolean;
  focusTrap?: boolean;
  disableEscapeKeyDown?: boolean;

  onClose(event?: React.SyntheticEvent): void;
  onClick?(event: React.MouseEvent): void;
  onKeyDown?(event: React.SyntheticEvent): void;
  'data-selector'?: string;
}

export function Modal(props: IModalProps) {
  const {
    children,
    hideBackdrop,
    backdrop = true,
    className,
    focusTrap = true,
    isOpen,
    keepMounted,
    onClick,
    resize = 'none',
    scalable = true,
  } = props;

  const ref = React.useRef<HTMLDivElement>(null);
  const [exited, setExited] = React.useState(true);

  const hasTransition = !!children.props.hasOwnProperty('in');

  useScrollBlock(!exited);
  useViewportResizer({
    mode: resize,
    disabled: exited,
    container: ref,
  });

  const childProps: {
    appear?: boolean;
    tabIndex?: string;
    onEnter?(): void;
    onExited?(): void;
  } = {};

  if (children.props.tabIndex === undefined) {
    childProps.tabIndex = '-1';
  }

  if (hasTransition) {
    childProps.appear = true;
    childProps.onEnter = handleEnter;
    childProps.onExited = handleExited;
  }

  if (!keepMounted && !isOpen && (!hasTransition || exited)) {
    return null;
  }

  return (
    <Portal>
      <FocusTrap
        active={!exited}
        paused={exited || !focusTrap}
        focusTrapOptions={{
          allowOutsideClick: true,
          escapeDeactivates: false,
        }}
      >
        <div
          ref={ref}
          onClick={onClick}
          data-hook={props['data-hook']}
          data-selector={props['data-selector']}
          onKeyDown={handleKeyDown}
          className={cls(classes.root, className, {
            [classes.exited]: exited,
          })}
        >
          {!backdrop ? null : (
            <Backdrop
              isOpen={isOpen}
              data-hook="modal-backdrop"
              transparent={hideBackdrop}
              onClick={handleBackdropClick}
            />
          )}
          {React.cloneElement(children, childProps)}
        </div>
      </FocusTrap>
    </Portal>
  );

  function handleKeyDown(event: React.KeyboardEvent) {
    props.onKeyDown?.(event);

    if (event.key !== 'Escape') {
      return;
    }

    if (!props.disableEscapeKeyDown) {
      event.stopPropagation();

      props.onClose(event);
    }
  }

  function handleEnter() {
    setExited(false);

    children.props.onEnter?.();
  }

  function handleExited() {
    setExited(true);

    children.props.onExited?.();
  }

  function handleBackdropClick(event: React.MouseEvent) {
    props.onClose(event);
  }
}

Modal.displayName = 'wui/Modal';
